import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

type Status = "idle" | "submitting" | "success" | "error";

export default function Feedback() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useDocumentMeta({
    title: "用户反馈与联系 - 美国式禁忌2",
    description:
      "向《美国式禁忌2》影视内容站提交内容纠错、投稿、合作或使用建议，我们会认真阅读每一条留言。",
    keywords: "美国式禁忌2,用户反馈,联系我们,内容纠错",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // anti-spam honeypot: silently treat as success for bots
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        payload.append(key, String(value));
      });

      const response = await fetch("https://readdy.ai/api/form/db78n06n6nb9f68e9atg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请检查内容后稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，提交失败，请稍后再试。");
    }
  };

  const inputClass =
    "w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 focus:border-primary-500 outline-none text-sm text-foreground-900 placeholder:text-foreground-500 transition-colors";

  return (
    <SiteLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-16">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "用户反馈" }]} />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          <div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              用户反馈
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              发现内容错误、想投稿剧情解析、或者单纯想聊聊这部剧，都可以在这里告诉我们。
              我们会认真阅读每一条留言，并在合适的时候回复。
            </p>

            <form
              id="feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-8 rounded-lg bg-background-100 border border-background-200 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-2">
                    称呼 <span className="text-primary-300">*</span>
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-2">
                    邮箱 <span className="text-primary-300">*</span>
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    autoComplete="email"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="fb-topic" className="block text-sm text-foreground-700 mb-2">
                  反馈类型 <span className="text-primary-300">*</span>
                </label>
                <select id="fb-topic" name="topic" required defaultValue="" className={`${inputClass} cursor-pointer`}>
                  <option value="" disabled className="bg-background-100">
                    请选择反馈类型
                  </option>
                  <option value="内容纠错" className="bg-background-100">内容纠错</option>
                  <option value="剧情投稿" className="bg-background-100">剧情投稿</option>
                  <option value="合作与版权" className="bg-background-100">合作与版权</option>
                  <option value="使用建议" className="bg-background-100">使用建议</option>
                  <option value="其他" className="bg-background-100">其他</option>
                </select>
              </div>

              <div className="mt-4">
                <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-2">
                  留言内容 <span className="text-primary-300">*</span>
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请简要描述你的问题或建议（最多 500 字）"
                  className="w-full rounded-md bg-background-50 border border-background-200 focus:border-primary-500 outline-none text-sm text-foreground-900 placeholder:text-foreground-500 p-4 resize-none transition-colors"
                />
              </div>

              <div className="contact-verify-field" aria-hidden="true">
                <label htmlFor="fb-website-alt">Website</label>
                <input
                  id="fb-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-foreground-500">
                  提交即表示你同意我们根据留言内容与你联系。
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full sm:w-auto h-11 px-8 rounded-md bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                >
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </div>

              {status === "success" && (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-accent-500/10 border border-accent-500/30 px-4 py-3 text-sm text-accent-300 animate-fade-in">
                  <i className="ri-checkbox-circle-line text-lg" />
                  反馈已提交，感谢你的留言！我们会尽快查看。
                </div>
              )}
              {status === "error" && (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-primary-600/15 border border-primary-500/40 px-4 py-3 text-sm text-primary-300 animate-fade-in">
                  <i className="ri-error-warning-line text-lg" />
                  {errorMsg || "提交失败，请稍后再试。"}
                </div>
              )}
            </form>
          </div>

          <aside className="space-y-5">
            <div className="rounded-lg bg-background-100 border border-background-200 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-accent-400" />
                反馈须知
              </h2>
              <ul className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                <li className="flex gap-2">
                  <i className="ri-time-line text-accent-400 mt-0.5" />
                  我们通常在 2–3 个工作日内查看留言。
                </li>
                <li className="flex gap-2">
                  <i className="ri-edit-2-line text-accent-400 mt-0.5" />
                  内容纠错请尽量附上剧集编号与具体位置。
                </li>
                <li className="flex gap-2">
                  <i className="ri-shield-cross-line text-accent-400 mt-0.5" />
                  本站不接受影片资源相关的索取或分享请求。
                </li>
              </ul>
            </div>
            <div className="rounded-lg bg-gradient-to-br from-primary-950 to-background-100 border border-background-200 p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-2">
                直接发邮件
              </h2>
              <p className="text-sm text-foreground-600 mb-4">也可以直接发送邮件给编辑部。</p>
              <a
                href="mailto:contact@americantaboo2.example.com"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-mail-open-line" />
                contact@americantaboo2...
              </a>
            </div>
            <div className="rounded-lg bg-background-100 border border-background-200 p-6">
              <p className="text-sm text-foreground-600 leading-relaxed">
                想先看看内容？
              </p>
              <Link
                to="/category"
                className="mt-3 inline-flex items-center gap-2 text-sm text-accent-400 hover:text-accent-300 cursor-pointer transition-colors"
              >
                浏览全部剧集
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}